<template>
  <div class="handlebar-filters">
    <h2>{{ t('common.properties') }}</h2>

    <BfFlex direction="column">
      <CommonFilterAccordion
        :title="t('setup.handlebar.bike')"
        icon="/icons/filters/bike.svg"
      >
        <CommonCheckFilters
          :filters="handlebarTypeFilters"
          @updated="search($event)"
          @summary="typeSummary = $event"
        />
      </CommonFilterAccordion>

      <CommonFilterAccordion
        :title="t('setup.handlebar.width')"
        :icon="widthIcon()"
      >
        <CommonSliderFilter
          :unit="t('common.unit.mm')"
          :summary-prefix="t('setup.handlebar.width')"
          :filter="handlebarWidthFilter"
          @updated="search($event)"
          @summary="widthSummary = $event"
        />
      </CommonFilterAccordion>

      <CommonFilterAccordion
        v-if="bikeHoodsType === BikeHoodsType.DROPBAR"
        :title="t('setup.handlebar.reach')"
        icon="/icons/filters/handlebar__road--reach.svg"
      >
        <CommonSliderFilter
          :unit="t('common.unit.mm')"
          :summary-prefix="t('setup.handlebar.reach')"
          :filter="handlebarReachFilter"
          @updated="search($event)"
          @summary="reachSummary = $event"
        />
      </CommonFilterAccordion>

      <CommonFilterAccordion
        v-if="bikeHoodsType === BikeHoodsType.DROPBAR"
        :title="t('setup.handlebar.drop')"
        icon="/icons/filters/handlebar__road--drop.svg"
      >
        <CommonSliderFilter
          :unit="t('common.unit.mm')"
          :summary-prefix="t('setup.handlebar.drop')"
          :filter="handlebarDropFilter"
          @updated="search($event)"
          @summary="dropSummary = $event"
        />
      </CommonFilterAccordion>

      <CommonFilterAccordion
        v-if="
          bikeHoodsType === BikeHoodsType.DROPBAR ||
          bikeHoodsType === BikeHoodsType.FLATBAR
        "
        :title="t('setup.handlebar.sweep')"
        :icon="sweepIcon()"
      >
        <CommonSliderFilter
          :unit="t('common.unit.degreesSign')"
          :summary-prefix="t('setup.handlebar.sweep')"
          :filter="handlebarSweepFilter"
          @updated="search($event)"
          @summary="sweepSummary = $event"
        />
      </CommonFilterAccordion>

      <CommonFilterAccordion
        v-if="bikeHoodsType === BikeHoodsType.DROPBAR"
        :title="t('setup.handlebar.flare')"
        icon="/icons/filters/handlebar__road--flare.svg"
      >
        <CommonSliderFilter
          :unit="t('common.unit.degreesSign')"
          :summary-prefix="t('setup.handlebar.flare')"
          :filter="handlebarFlareFilter"
          @updated="search($event)"
          @summary="sweepSummary = $event"
        />
      </CommonFilterAccordion>
    </BfFlex>

    <FilterSummary>
      <FilterSummaryItem v-if="typeSummary">{{
        typeSummary
      }}</FilterSummaryItem>
      <FilterSummaryItem v-if="widthSummary">{{
        widthSummary
      }}</FilterSummaryItem>
      <FilterSummaryItem v-if="reachSummary">{{
        reachSummary
      }}</FilterSummaryItem>
      <FilterSummaryItem v-if="dropSummary">{{
        dropSummary
      }}</FilterSummaryItem>
      <FilterSummaryItem v-if="sweepSummary">{{
        sweepSummary
      }}</FilterSummaryItem>
      <FilterSummaryItem v-if="flareSummary">{{
        flareSummary
      }}</FilterSummaryItem>
    </FilterSummary>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { BfFlex } from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/store/userStore'
import { useHandlebarStore } from '@/store/handlebarStore'
import { useBikeStore } from '@/store/bikeStore'
import handlebarService from '@/service/handlebarService'
import type {
  CommonCheckFilterType,
  CommonSliderFilterType,
} from '@/types/filter'
import { BikeHoodsType } from '@/types/bike'
import FilterSummary from '@/components/setup/FilterSummary.vue'
import FilterSummaryItem from '@/components/setup/FilterSummaryItem.vue'
import CommonFilterAccordion from '@/components/setup/common/CommonFilterAccordion.vue'
import CommonCheckFilters from '@/components/setup/common/CommonCheckFilters.vue'
import CommonSliderFilter from '@/components/setup/common/CommonSliderFilter.vue'

const { t } = useI18n()

const userStore = useUserStore()
const { sessionId } = storeToRefs(userStore)

const handlebarStore = useHandlebarStore()
const { filters } = storeToRefs(handlebarStore)

const bikeStore = useBikeStore()
const { bikeHoodsType } = storeToRefs(bikeStore)

const typeSummary = ref()
const widthSummary = ref()
const reachSummary = ref()
const dropSummary = ref()
const sweepSummary = ref()
const flareSummary = ref()

const loading = ref(false)

const widthIcon = () => {
  return handlebarService.getWidthIcon(bikeHoodsType.value)
}

const sweepIcon = () => {
  return handlebarService.getSweepIcon(bikeHoodsType.value)
}

const handlebarTypeFilters: CommonCheckFilterType = {
  TypeRoad: {
    label: t('common.bike.type.road'),
    icons: {
      default: '/icons/filters/bike__road.svg',
      selected: '/icons/filters/bike__road--selected.svg',
    },
  },
  TypeGravel: {
    label: t('common.bike.type.gravel'),
    icons: {
      default: '/icons/filters/bike__gravel.svg',
      selected: '/icons/filters/bike__gravel--selected.svg',
    },
  },
  TypeCyclocross: {
    label: t('common.bike.type.cyclocross'),
    icons: {
      default: '/icons/filters/bike__cyclocross.svg',
      selected: '/icons/filters/bike__cyclocross--selected.svg',
    },
  },
  TypeTimeTrial: {
    label: t('common.bike.type.timeTrial'),
    icons: {
      default: '/icons/filters/bike__time-trial.svg',
      selected: '/icons/filters/bike__time-trial--selected.svg',
    },
  },
  TypeFullTriathlon: {
    label: t('common.bike.type.fullTriathlon'),
    icons: {
      default: '/icons/filters/bike__trifull.svg',
      selected: '/icons/filters/bike__trifull--selected.svg',
    },
  },
  TypeShortTriathlon: {
    label: t('common.bike.type.shortTriathlon'),
    icons: {
      default: '/icons/filters/bike__trishort.svg',
      selected: '/icons/filters/bike__trishort--selected.svg',
    },
  },
  TypeMtbCrossCountry: {
    label: t('common.bike.type.mtbCrossCountry'),
    icons: {
      default: '/icons/filters/bike__mtb-cx.svg',
      selected: '/icons/filters/bike__mtb-cx--selected.svg',
    },
  },
  TypeMtbTrail: {
    label: t('common.bike.type.mtbTrail'),
    icons: {
      default: '/icons/filters/bike__mtb-trial.svg',
      selected: '/icons/filters/bike__mtb-trial--selected.svg',
    },
  },
  TypeTrekking: {
    label: t('common.bike.type.trekking'),
    icons: {
      default: '/icons/filters/bike__trekking.svg',
      selected: '/icons/filters/bike__trekking--selected.svg',
    },
  },
  TypeCity: {
    label: t('common.bike.type.city'),
    icons: {
      default: '/icons/filters/bike__city.svg',
      selected: '/icons/filters/bike__city--selected.svg',
    },
  },
}

const handlebarWidthFilter: CommonSliderFilterType = {
  value: {
    Width: filters.value.Width ?? 300,
  },
  min: {
    MinWidth: 300,
  },
  max: {
    MaxWidth: 1000,
  },
}

const handlebarReachFilter: CommonSliderFilterType = {
  value: {
    Reach: filters.value.Reach ?? 0,
  },
  min: {
    MinReach: 0,
  },
  max: {
    MaxReach: 200,
  },
}

const handlebarDropFilter: CommonSliderFilterType = {
  value: {
    Drop: filters.value.Drop ?? 0,
  },
  min: {
    MinDrop: 0,
  },
  max: {
    MaxDrop: 200,
  },
}

const handlebarSweepFilter: CommonSliderFilterType = {
  value: {
    Sweep: filters.value.Sweep ?? 0,
  },
  min: {
    MinSweep: 0,
  },
  max: {
    MaxSweep: 45,
  },
}

const handlebarFlareFilter: CommonSliderFilterType = {
  value: {
    Flare: filters.value.Flare ?? 0,
  },
  min: {
    MinFlare: 0,
  },
  max: {
    MaxFlare: 36,
  },
}

const search = (e?: Record<string, any>) => {
  handlebarStore.reset()

  if (e) handlebarStore.setFilters(e)

  handlebarStore.retrieveHandlebars(sessionId?.value ?? '').then(() => {
    loading.value = false
  })
}
</script>

<style lang="scss">
.handlebar-filters {
  padding: 2.4rem;
}
</style>
